/**
 * Lógica pura da fila de repasse manual à Equatorial.
 *
 * Vive fora do componente por dois motivos: a conta de atraso tem armadilha de fuso
 * (ver `diasDeAtraso`) e a ordenação decide a ordem em que o dinheiro sai — as duas
 * coisas merecem teste, e teste de componente aqui seria caro sem ganho.
 *
 * Ver views/Financeiro/RepasseEquatorialManualView.tsx e docs/PLAN-repasse-equatorial-manual.md.
 */

/** O mínimo que as funções abaixo precisam saber de um item da fila. */
export interface ItemRepasse {
  valor_equatorial: number | string | null;
  linha_digitavel?: string | null;
  pix_copia_cola?: string | null;
  faturas?: { data_vencimento?: string | null } | null;
}

/**
 * Dias entre hoje e o vencimento da distribuidora. Positivo = atrasado, 0 = vence hoje,
 * negativo = ainda tem prazo. `null` quando não há vencimento.
 *
 * A data vem do Postgres como 'YYYY-MM-DD' (ou ISO completo). `new Date('2026-08-20')`
 * seria interpretado como MEIA-NOITE UTC e, em GMT-3, voltaria como dia 19 — é a mesma
 * troca de fuso que já mordeu o export de faturas. Por isso os campos são quebrados na
 * mão e o Date é montado no fuso local.
 */
export const diasDeAtraso = (vencimento: string | null | undefined, hoje: Date = new Date()): number | null => {
  if (!vencimento) return null;
  const [ano, mes, dia] = vencimento.split('T')[0].split('-').map(Number);
  if (!ano || !mes || !dia) return null;
  const venc = new Date(ano, mes - 1, dia);
  venc.setHours(0, 0, 0, 0);
  const referencia = new Date(hoje.getTime());
  referencia.setHours(0, 0, 0, 0);
  return Math.round((referencia.getTime() - venc.getTime()) / 86_400_000);
};

/** Vencido = o prazo da distribuidora já passou. Vencer hoje ainda não é atraso. */
export const estaVencido = (item: ItemRepasse, hoje: Date = new Date()): boolean =>
  (diasDeAtraso(item.faturas?.data_vencimento, hoje) ?? -1) > 0;

/**
 * Sem código de barras nem PIX não dá para pagar: precisa de segunda via na distribuidora.
 * O modo manual deixa esses itens entrarem na fila de propósito — são justamente os que
 * dão mais trabalho, e escondê-los seria o pior resultado.
 */
export const precisaSegundaVia = (item: ItemRepasse): boolean =>
  !item.linha_digitavel && !item.pix_copia_cola;

/**
 * Ordena pelo vencimento da distribuidora, mais antigo primeiro — a ordem em que o
 * dinheiro precisa sair. Item sem vencimento vai para o fim, não para o começo: ele não
 * tem prazo conhecido e não pode empurrar para baixo quem está vencendo hoje.
 */
export const ordenarPorVencimento = <T extends ItemRepasse>(itens: readonly T[]): T[] =>
  [...itens].sort((a, b) => {
    const va = a.faturas?.data_vencimento ?? '9999-12-31';
    const vb = b.faturas?.data_vencimento ?? '9999-12-31';
    return va.localeCompare(vb);
  });

/**
 * Frase legível de `pagamento_equatorial_fila.error_details`.
 *
 * Desde 02/09/2026 a edge grava "motivo legível | {json cru}" quando reconhece o erro
 * (ver classificarErroAsaas). Item antigo, ou falha que ninguém classificou, tem só o
 * JSON — nesse caso devolve null, porque despejar o corpo do banco na tela é pior do
 * que não mostrar nada. Texto livre escrito por humano (as sentinelas de agosto) passa
 * inteiro, que é o que se quer.
 */
export const motivoDaFalha = (errorDetails: string | null | undefined): string | null => {
  const motivo = (errorDetails ?? '').split(' | ')[0].trim();
  if (!motivo || motivo.startsWith('{') || motivo.startsWith('[')) return null;
  return motivo;
};

/** Soma os valores da distribuidora, tolerando o numeric do Postgres que chega como string. */
export const somarValores = (itens: readonly ItemRepasse[]): number =>
  itens.reduce((acc, i) => acc + (Number(i.valor_equatorial) || 0), 0);

// --- Explicação do repasse para a Auditoria de Pagamentos ------------------------
//
// A auditoria mostrava só um selo ("Repasse falhou") e, aberto, o texto cru que a edge
// grava — "Sicoob recusou o token da conta associacao (escopos ...): HTTP 400: {...}".
// O Financeiro precisa de três coisas por fatura: em que pé está, por quê, e o que fazer.

/** O que a explicação precisa de um item de pagamento_equatorial_fila. */
export interface ItemFilaRepasse {
  status: string;
  error_details?: string | null;
  gateway?: string | null;
  conta_pagadora?: string | null;
  resultado?: Record<string, unknown> | null;
  asaas_bill_id?: string | null;
  baixa_em?: string | null;
  baixa_por_nome?: string | null;
  baixa_observacao?: string | null;
  proxima_tentativa_em?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export type TomRepasse = 'ok' | 'erro' | 'andamento' | 'neutro';

export interface ExplicacaoRepasse {
  /** A situação em poucas palavras. */
  titulo: string;
  /** O porquê, na língua de quem opera. */
  motivo: string | null;
  /** O que fazer agora; null quando não há nada a fazer. */
  acao: string | null;
  tom: TomRepasse;
}

const PRIORIDADE_ITEM: Record<string, number> = {
  concluido: 3, pago_manual: 3,
  pendente: 2, processando: 2, aguardando_confirmacao: 2, manual: 2,
  falhou: 1,
};

/**
 * Uma fatura pode ter vários itens na fila (o que falhou e o que foi reenviado depois).
 * O que diz a situação de hoje é o pago, senão o que está andando, senão a falha mais
 * recente.
 */
export const escolherItemRepasse = <T extends ItemFilaRepasse>(itens: readonly T[]): T | null => {
  let melhor: T | null = null;
  for (const i of itens) {
    if (!melhor) { melhor = i; continue; }
    const pi = PRIORIDADE_ITEM[i.status] ?? 0;
    const pm = PRIORIDADE_ITEM[melhor.status] ?? 0;
    const recente = (i.updated_at ?? i.created_at ?? '') > (melhor.updated_at ?? melhor.created_at ?? '');
    if (pi > pm || (pi === pm && recente)) melhor = i;
  }
  return melhor;
};

const semAcento = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// O botão fica no próprio painel da Auditoria desde 03/11/2026 (e segue na fatura).
const PEGAR_DE_NOVO = 'Se a conta de luz continuar em aberto, use “Pagar Equatorial agora”.';

// Ordem importa: "cobra R$ 0.00 hoje" chega na mesma frase dos "encargos acima de 10%".
const ERROS_CONHECIDOS: { padrao: RegExp; motivo: string; acao: string | null; tom?: TomRepasse }[] = [
  { padrao: /invalid_scope|recusou o token/,
    motivo: 'O Sicoob recusou a autorização: o aplicativo da conta ainda não tinha permissão para pagar boletos.',
    acao: `Corrigido em 20/10/2026 com o app novo. ${PEGAR_DE_NOVO}` },
  { padrao: /10039|nao esta ativo para pagamento/,
    motivo: 'A conta no Sicoob ainda não estava habilitada para pagar boletos.',
    acao: `Associação e Gestão foram habilitadas em 20/10/2026. ${PEGAR_DE_NOVO}` },
  { padrao: /webhook_url nao configurado/,
    motivo: 'O pagamento automático pelo Sicoob ainda não estava ligado quando o cliente pagou.',
    acao: PEGAR_DE_NOVO },
  { padrao: /r\$ 0[.,]00 hoje|ja foi efetuado|ja foi pago/,
    motivo: 'A conta de luz já estava paga quando o sistema foi pagar.',
    acao: 'Não pague de novo. Descubra quem pagou e dê baixa em Repasse Equatorial — manual.' },
  // Crystyano, 02/11/2026 (Finados): em dia não útil "pagar hoje" vira agendamento, e o
  // Sicoob não agenda boleto vencido. A fila passou a esperar o dia útil seguinte.
  { padrao: /\b10017\b|agendar um boleto vencido/,
    motivo: 'Em feriado ou fim de semana o Sicoob não paga conta de luz vencida: só em dia útil.',
    acao: 'Em dia útil, se a conta de luz continuar em aberto, use “Pagar Equatorial agora”.' },
  { padrao: /unregistered_bank_slip|nao registrado na rede bancaria/,
    motivo: 'A conta de luz ainda não estava registrada no banco quando o sistema foi pagar.',
    acao: PEGAR_DE_NOVO },
  { padrao: /nao cobrou a equatorial/,
    motivo: 'O boleto que o cliente pagou cobrou só a parte da Vigor, sem a conta de luz.',
    acao: 'A conta de luz fica com o cliente, ou a Vigor paga por decisão do Financeiro.' },
  { padrao: /valor divergente/,
    motivo: 'O valor da conta de luz no banco é diferente do que está na fatura.',
    acao: 'Confira a fatura da Equatorial antes de pagar.' },
  { padrao: /nao e da equatorial/,
    motivo: 'O código de barras da fatura não é de uma conta da Equatorial. Nada foi pago.',
    acao: 'Confira a linha digitável da conta de luz na fatura.' },
  { padrao: /limite de pagamento|2a via|segunda via/,
    motivo: 'A conta de luz passou do prazo em que o banco ainda aceita pagamento.',
    acao: 'Peça a 2ª via à Equatorial e atualize a fatura.' },
  { padrao: /encargos acima/,
    motivo: 'O banco cobraria juros e multa acima de 10% da conta de luz.',
    acao: 'Confira a conta de luz antes de pagar.' },
  { padrao: /linha digitavel.*invalida/,
    motivo: 'A linha digitável da conta de luz está incompleta ou errada.',
    acao: 'Corrija a linha digitável da Equatorial na fatura.' },
  { padrao: /horario bancario|ate as 23 horas/,
    motivo: 'Esperando o horário bancário para pagar.', acao: null, tom: 'andamento' },
  { padrao: /assinatura/,
    motivo: 'O pagamento entrou no banco e espera alguém assinar no app.',
    acao: 'Assine o pagamento no app ou internet banking.', tom: 'andamento' },
  { padrao: /saldo/,
    motivo: 'Faltou saldo na conta para pagar a conta de luz.',
    acao: 'Reponha o saldo da conta. O sistema tenta de novo.' },
];

/**
 * A frase de dentro do JSON que o banco devolve: `errors[].description` (Asaas) ou
 * `mensagens[].mensagem` (Sicoob). O Asaas grava o JSON cru, sem frase antes — sem isto a
 * tela dizia "o sistema não registrou o motivo" com o motivo gravado (fatura 9644).
 */
const descricaoDoJson = (errorDetails: string): string | null => {
  const inicio = errorDetails.indexOf('{');
  if (inicio < 0) return null;
  try {
    const j = JSON.parse(errorDetails.slice(inicio));
    const lista: unknown[] = Array.isArray(j?.errors) ? j.errors : Array.isArray(j?.mensagens) ? j.mensagens : [];
    const frases = lista
      .map(e => (e as { description?: unknown; mensagem?: unknown }))
      .map(e => String(e.description ?? e.mensagem ?? '').trim())
      .filter(Boolean);
    return frases.length ? frases.join(' ') : null;
  } catch {
    return null;
  }
};

/** Traduz o texto técnico que a edge grava. null quando não há o que dizer. */
export const traduzirErroRepasse = (errorDetails: string | null | undefined):
  { motivo: string; acao: string | null; tom: TomRepasse } | null => {
  if (!errorDetails) return null;
  const texto = semAcento(errorDetails);
  for (const e of ERROS_CONHECIDOS) {
    if (e.padrao.test(texto)) return { motivo: e.motivo, acao: e.acao, tom: e.tom ?? 'erro' };
  }
  // Frase com JSON dentro ("HTTP 400: {...}") perde para a descrição que está no JSON.
  const frase = motivoDaFalha(errorDetails);
  const livre = frase && !frase.includes('{') ? frase : descricaoDoJson(errorDetails) ?? frase;
  return livre ? { motivo: livre, acao: null, tom: 'erro' } : null;
};

/** 'YYYY-MM-DD' no dia certo (ver diasDeAtraso); ISO completo no fuso local. */
const formatarData = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const NOME_CONTA: Record<string, string> = {
  associacao: 'Sicoob da Associação', gestao: 'Sicoob da Gestão', hsolar: 'Sicoob da HSolar',
  vigor: 'Asaas da Vigor', parceira: 'Asaas da parceira',
};

const deOndeSaiu = (item: ItemFilaRepasse): string => {
  const r = item.resultado ?? {};
  if (item.gateway === 'sicoob') return NOME_CONTA[String(r.conta ?? '')] ?? 'Sicoob';
  return NOME_CONTA[item.conta_pagadora ?? ''] ?? 'Asaas';
};

/** Valor gravado nos 10 últimos dígitos da linha digitável (o título registrado). */
const valorNaLinha = (linha: string | null | undefined): number | null => {
  const d = String(linha ?? '').replace(/\D/g, '');
  return d.length === 47 && d[0] !== '8' ? Number(d.slice(37)) / 100 : null;
};

/**
 * O boleto que o cliente pagou cobrou a conta de luz? Só para fatura da Vigor (Sicoob):
 * a marca gravada pelo banco ou, nas faturas anteriores à marca, a linha digitável cobrando
 * mais que a parte da Vigor. null quando não dá para saber (parceira, que segue regra
 * própria no Asaas, ou boleto sem linha).
 *
 * Faturas assim não tinham conta de luz a repassar: boleto emitido antes de a UC aderir,
 * ou antes da correção do n8n de 14/10/2026 (Vilmar e Kamila, emitidos em 13/10).
 */
export const boletoCobrouEquatorial = (f: {
  empresa_parceira_id?: string | null;
  valor_equatorial_no_boleto?: number | string | null;
  linha_digitavel_boleto?: string | null;
  valor_boleto_vigor?: number | string | null;
}): boolean | null => {
  if (f.empresa_parceira_id) return null;
  if (Number(f.valor_equatorial_no_boleto) > 0) return true;
  const cobrado = valorNaLinha(f.linha_digitavel_boleto);
  if (cobrado == null) return null;
  return cobrado - (Number(f.valor_boleto_vigor) || 0) > 0.009;
};

const STATUS_DE_FALHA = new Set(['falhou', 'uc_nao_encontrada', 'dados_indisponiveis']);

/**
 * Situação do repasse à Equatorial de uma fatura, a partir de
 * `faturas.status_repasse_equatorial` e do item da fila que a representa
 * (ver escolherItemRepasse). null quando a fatura não tem repasse (fora do boleto único).
 *
 * `cobrouEquatorial` (ver boletoCobrouEquatorial) vence o status: se o boleto não cobrou a
 * conta de luz, a falha da fila não é falha de repasse — não havia o que repassar.
 */
export const explicarRepasse = (
  status: string | null | undefined,
  item: ItemFilaRepasse | null,
  cobrouEquatorial: boolean | null = null,
): ExplicacaoRepasse | null => {
  if (!status || status === 'nao_aplicavel') return null;
  if (cobrouEquatorial === false && STATUS_DE_FALHA.has(status)) {
    return status === 'uc_nao_encontrada'
      ? {
        titulo: 'Fora do boleto único',
        motivo: 'A UC não está no cadastro e o boleto cobrou só a parte da Vigor: não há conta de luz para repassar.',
        acao: 'Vale corrigir o número da UC no cadastro.',
        tom: 'neutro',
      }
      : {
        titulo: 'Boleto sem a conta de luz',
        motivo: 'O boleto que o cliente pagou cobrou só a parte da Vigor, então não havia conta de luz para repassar. Acontece quando o boleto saiu antes de a UC aderir, ou antes da correção de 14/10/2026.',
        acao: 'A conta de luz é do cliente. Se o Financeiro decidir que a Vigor paga, use Repasse Equatorial — manual.',
        tom: 'neutro',
      };
  }
  const erro = traduzirErroRepasse(item?.error_details);

  switch (status) {
    case 'pago': {
      // Baixa manual (dar_baixa_repasse_equatorial): a conta de luz foi paga fora do
      // automático — pelo Financeiro no banco ou pelo próprio cliente. A observação diz quem.
      if (item?.status === 'pago_manual') {
        const quando = formatarData(item.baixa_em);
        const quem = item.baixa_por_nome ? ` por ${item.baixa_por_nome}` : '';
        const obs = item.baixa_observacao ? ` — “${item.baixa_observacao}”` : '';
        return { titulo: 'Paga por fora', motivo: `Registrado${quem}${quando ? ` em ${quando}` : ''}${obs}.`, acao: null, tom: 'ok' };
      }
      if (!item) return { titulo: 'Equatorial paga', motivo: null, acao: null, tom: 'ok' };
      const r = item.resultado ?? {};
      const quando = formatarData((r.dataPagamento as string) ?? item.updated_at);
      const comprovante = r.idPagamento ? ` · pagamento nº ${r.idPagamento}` : item.asaas_bill_id ? ` · ${item.asaas_bill_id}` : '';
      return { titulo: 'Equatorial paga', motivo: `Pelo ${deOndeSaiu(item)}${quando ? ` em ${quando}` : ''}${comprovante}.`, acao: null, tom: 'ok' };
    }
    case 'falhou':
      return {
        titulo: 'Repasse falhou',
        motivo: erro?.motivo ?? 'O sistema não registrou o motivo desta falha.',
        acao: erro?.acao ?? `Confira a conta de luz antes de pagar. ${PEGAR_DE_NOVO}`,
        tom: 'erro',
      };
    case 'pendente': {
      const quando = item?.proxima_tentativa_em ? new Date(item.proxima_tentativa_em) : null;
      const proxima = quando && !Number.isNaN(quando.getTime())
        ? `Próxima tentativa em ${quando.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.`
        : 'O sistema paga em dia útil, das 8h às 18h.';
      return { titulo: 'Na fila para pagar', motivo: erro?.motivo ?? proxima, acao: erro?.motivo ? proxima : null, tom: 'andamento' };
    }
    case 'aguardando_confirmacao':
      return {
        titulo: 'Esperando o banco confirmar',
        motivo: erro?.motivo ?? 'O pagamento foi enviado; falta o banco confirmar.',
        acao: erro?.tom === 'andamento' ? erro.acao : null,
        tom: 'andamento',
      };
    case 'manual_pendente':
      // Boleto sem a conta de luz no manual: só chega aqui posto à mão — o gatilho não
      // enfileira esse caso. Igor e Crystyano (SET/2026) caíram assim e saíram em 21/10/2026:
      // a conta era do cliente. Não afirmar que alguém decidiu pagar; pedir para conferir.
      if (cobrouEquatorial === false) {
        return {
          titulo: 'Repasse manual',
          motivo: 'O boleto cobrou só a parte da Vigor: a conta de luz é do cliente, mas está aqui para a Vigor pagar.',
          acao: 'Confirme se a Vigor deve mesmo pagar antes de pagar pelo internet banking.',
          tom: 'andamento',
        };
      }
      return {
        titulo: 'Repasse manual',
        motivo: 'Esta UC paga a conta de luz à mão, fora do automático.',
        acao: 'Pague pelo internet banking e dê baixa em Repasse Equatorial — manual.',
        tom: 'andamento',
      };
    case 'dados_indisponiveis':
      return {
        titulo: 'Falta a conta de luz',
        motivo: 'A fatura não tem o código de barras da Equatorial, então o sistema não tem o que pagar.',
        acao: `Suba a fatura da Equatorial com o código de barras. ${PEGAR_DE_NOVO}`,
        tom: 'erro',
      };
    case 'uc_nao_encontrada':
      return {
        titulo: 'UC não encontrada',
        motivo: 'O número da UC desta fatura não bate com nenhuma UC do cadastro, e sem a UC o sistema não sabe se ela tem boleto único.',
        acao: 'Corrija o número da UC no cadastro ou na fatura.',
        tom: 'erro',
      };
    case 'quitado_pelo_cliente':
      return { titulo: 'Cliente pagou a conta de luz', motivo: 'O cliente quitou a Equatorial por fora; o boleto não cobrou essa parte.', acao: null, tom: 'neutro' };
    case 'debito_automatico':
      return { titulo: 'Débito automático', motivo: 'A Equatorial debita a conta de luz direto do cliente.', acao: null, tom: 'neutro' };
    case 'reembolsado':
      return { titulo: 'Reembolsado', motivo: null, acao: null, tom: 'neutro' };
    default:
      return { titulo: status, motivo: erro?.motivo ?? null, acao: erro?.acao ?? null, tom: erro ? 'erro' : 'neutro' };
  }
};
